import { useState } from "react";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

// Round 25 · admin-only "Authorised early start" tick on an early_in row.
// Ticking or unticking needs a reason; the backend recalculates that one day.
export default function EarlyStartOverride({ pid, ex, canEdit, onSaved }) {
    const o = ex.early_start_override;
    const authorised = !!o?.authorised;
    const [pending, setPending] = useState(null);
    const [reason, setReason] = useState("");
    const [saving, setSaving] = useState(false);
    const tid = `${ex.employee_id}-${ex.date}`;

    const save = async () => {
        if (!reason.trim()) { toast.error("A reason is required"); return; }
        setSaving(true);
        try {
            const { data } = await api.patch(`/pay-periods/${pid}/window-exceptions/${ex.employee_id}/override`,
                { date: ex.date, authorised: pending, reason: reason.trim() });
            const d = data.delta_hours || {};
            toast.success(`Day recalculated · reg ${d.regular >= 0 ? "+" : ""}${d.regular}h, ×1.5 ${d.time_and_half >= 0 ? "+" : ""}${d.time_and_half}h`);
            setPending(null); setReason("");
            onSaved?.();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Override failed");
        } finally { setSaving(false); }
    };

    return (
        <div className="px-4 pb-2 pl-8 text-[12px]" data-testid={`early-start-override-${tid}`}>
            <div className="flex flex-wrap items-center gap-3">
                <label className="inline-flex items-center gap-1.5 select-none text-neutral-700">
                    <input type="checkbox" data-testid={`early-start-authorise-checkbox-${tid}`}
                           checked={pending ?? authorised} disabled={!canEdit || saving}
                           onChange={(e) => setPending(e.target.checked === authorised ? null : e.target.checked)}
                           className="accent-[color:var(--kp-success)]" />
                    Authorised early start
                </label>
                {o && (
                    <span data-testid={`early-start-override-meta-${tid}`} className="inline-flex items-center gap-1 text-neutral-500">
                        <ShieldCheck size={11} style={{ color: authorised ? "#0A8A3C" : "#8A6D00" }} />
                        {authorised ? "Authorised" : "Revoked"} by {o.by} · {(o.at || "").slice(0, 16).replace("T", " ")} · “{o.reason}”
                    </span>
                )}
                {!authorised && !o && <span className="text-neutral-500">Paid from normal start until authorised.</span>}
            </div>
            {pending !== null && (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                    <input data-testid={`early-start-reason-input-${tid}`} value={reason} autoFocus
                           onChange={(e) => setReason(e.target.value)} placeholder="Reason (required)"
                           className="kp-focus flex-1 min-w-[200px] border border-neutral-300 px-2 py-1 rounded-[3px]" />
                    <button data-testid={`early-start-save-btn-${tid}`} onClick={save} disabled={saving || !reason.trim()}
                            className="px-3 py-1 text-white text-[11px] font-display font-semibold uppercase tracking-[0.1em] rounded-[3px] disabled:opacity-50"
                            style={{ background: "var(--kp-red)" }}>
                        {saving ? "Saving…" : pending ? "Authorise & recalc day" : "Revoke & recalc day"}
                    </button>
                    <button data-testid={`early-start-cancel-btn-${tid}`} onClick={() => { setPending(null); setReason(""); }}
                            className="px-3 py-1 text-[11px] text-neutral-600 border border-neutral-300 rounded-[3px]">Cancel</button>
                </div>
            )}
        </div>
    );
}
